import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import TopBar from "./TopBar";
import Navbar from "./Navbar";
import Contact from "./Contact";
import SectionCurve from "./SectionCurve";
import { ShapeSquare, ShapeCircle, ShapeFlower, ShapeRainbow } from "./brand/Shapes";
import { ChevronRight } from "lucide-react";

/**
 * Shared layout for all sub-pages (About, Curriculum, Programmes…).
 * - TopBar + Navbar (Stonehill-style)
 * - Editorial page hero with eyebrow, big serif title, intro, decorative shapes
 * - <main> children
 * - Footer reuses Contact section (which contains the dark navy footer)
 */
export default function SubPageLayout({
  eyebrow,
  title,
  subtitle,
  accent = "cornflower",
  decor = "square",
  children,
  hideFooterContact = false,
}) {
  const location = useLocation();

  useEffect(() => {
    // Anchor scrolling
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      }
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [location.pathname, location.hash]);

  const accentMap = {
    cornflower: "#648DE5",
    lime: "#C6D947",
    teal: "#5BC4B8",
    turquoise: "#3AB0D6",
  };

  return (
    <div className="bg-[#FAFAF5] min-h-screen flex flex-col" data-testid="subpage">
      <TopBar />
      <Navbar />

      {/* Page hero */}
      <section className="relative pt-32 md:pt-60 pb-10 md:pb-20 overflow-hidden bg-[#E3EDF9]">
        {decor === "square" && (
          <ShapeSquare size={170} color={accentMap[accent]} className="absolute -top-10 right-[6%] floaty opacity-90 hidden md:block" />
        )}
        {decor === "circle" && (
          <ShapeCircle size={120} color={accentMap[accent]} className="absolute top-32 right-[8%] floaty opacity-90 hidden md:block" />
        )}
        {decor === "flower" && (
          <ShapeFlower size={120} color={accentMap[accent]} className="absolute top-32 right-[8%] floaty hidden md:block" />
        )}
        {decor === "rainbow" && (
          <ShapeRainbow size={140} color={accentMap[accent]} className="absolute top-44 right-[8%] floaty hidden md:block" />
        )}
        <ShapeCircle size={70} color="#3AB0D6" className="absolute bottom-10 left-[5%] floaty opacity-80 hidden md:block" />

        <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
          <Breadcrumbs />
          <span
            className="text-xs font-bold tracking-[0.22em] uppercase mb-5 mt-3 block"
            style={{ color: accentMap[accent] }}
            data-testid="page-eyebrow"
          >
            {eyebrow}
          </span>
          <h1
            className="font-display text-[#0F1B47] text-[40px] sm:text-6xl lg:text-7xl leading-[1.06] tracking-tight font-semibold max-w-4xl"
            data-testid="page-title"
          >
            {title}
          </h1>
          {subtitle && (
            <p className="mt-5 md:mt-6 max-w-2xl text-[#4A5568] text-[15px] md:text-lg leading-relaxed font-light">
              {subtitle}
            </p>
          )}
        </div>
      </section>

      <SectionCurve bg="#E3EDF9" fill="#FAFAF5" variant="wave" />

      <main className="flex-1 bg-[#FAFAF5]">{children}</main>

      {!hideFooterContact && (
        <>
          <SectionCurve bg="#FAFAF5" fill="#E3EDF9" variant="tilt" />
          <Contact />
        </>
      )}
    </div>
  );
}

function Breadcrumbs() {
  const location = useLocation();
  const parts = location.pathname.split("/").filter(Boolean);
  return (
    <nav
      className="flex items-center gap-1 text-xs font-semibold text-[#4A5568] tracking-wide"
      data-testid="breadcrumbs"
    >
      <Link to="/" className="hover:text-[#0F1B47]">Home</Link>
      {parts.map((p, i) => {
        const path = "/" + parts.slice(0, i + 1).join("/");
        const label = p.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        const isLast = i === parts.length - 1;
        return (
          <React.Fragment key={path}>
            <ChevronRight size={12} className="opacity-50" />
            {isLast ? (
              <span className="text-[#0F1B47]">{label}</span>
            ) : (
              <Link to={path} className="hover:text-[#0F1B47]">{label}</Link>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}
